import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import EditNoteIcon from '@mui/icons-material/EditNote';

import { hasPermission } from '../../utils/permissionUtils';
import type { IUser } from '../../types/damTypes';

import TrendsBrandRadarPage from './TrendsBrandRadarPage';
import TrendsEditorialPage from './TrendsEditorialPage';
import TrendsOpportunitiesPage from './TrendsOpportunitiesPage';
import TrendsOverviewPage from './TrendsOverviewPage';
import TrendsSectionPage from './TrendsSectionPage';
import TrendsSourceAdminPage from './TrendsSourceAdminPage';
import { TrendChannelProvider } from './channelFilter';
import { DISPLAY_FONT, NAV_SX, TREND_COLORS } from './trendTheme';

export type TrendsModuleView =
  | 'overview'
  | 'opportunities'
  | 'design'
  | 'food'
  | 'market'
  | 'brand_radar'
  | 'editorial'
  | 'sources';

const ALL_VIEWS: TrendsModuleView[] = [
  'overview',
  'opportunities',
  'design',
  'food',
  'market',
  'brand_radar',
  'editorial',
  'sources',
];

type TrendsModuleProps = {
  currentUser: IUser | null;
  onClose?: () => void;
};

function TrendsModuleInner({ currentUser, onClose }: TrendsModuleProps) {
  const [view, setView] = useState<TrendsModuleView>(() => {
    const hashView = window.location.hash.replace(/^#trends:/, '') as TrendsModuleView;
    return ALL_VIEWS.includes(hashView) ? hashView : 'overview';
  });

  const canReview = useMemo(() => currentUser ? hasPermission(currentUser, 'trends:review') : false, [currentUser]);
  const canManageSources = useMemo(() => currentUser ? hasPermission(currentUser, 'trends:manageSources') : false, [currentUser]);
  const canAdmin = useMemo(() => currentUser ? hasPermission(currentUser, 'trends:admin') : false, [currentUser]);
  const canUseEditorial = canReview || canAdmin;
  const navigateToView = (nextView: TrendsModuleView) => {
    setView(nextView);
    window.history.replaceState(null, '', `#trends:${nextView}`);
  };

  const readerNav: Array<{ label: string; view: TrendsModuleView }> = [
    { label: 'Översikt', view: 'overview' },
    { label: 'Affärs-\nmöjligheter', view: 'opportunities' },
    { label: 'Design &\nförpackning', view: 'design' },
    { label: 'Mat &\nRecept', view: 'food' },
    { label: 'Marknad &\nkonkurrens', view: 'market' },
    { label: 'Brand\nRadar', view: 'brand_radar' },
  ];

  const adminNav: Array<{ label: string; view: TrendsModuleView; icon: React.ReactNode }> = [
    ...(canUseEditorial ? [{ label: 'Redaktionellt', view: 'editorial' as const, icon: <EditNoteIcon sx={{ fontSize: 16 }} /> }] : []),
    ...(canManageSources ? [{ label: canAdmin ? 'Kontrollcenter\nAdmin' : 'Kontrollcenter', view: 'sources' as const, icon: <SettingsIcon sx={{ fontSize: 16 }} /> }] : []),
  ];

  let content: React.ReactNode;
  switch (view) {
    case 'overview':
      content = <TrendsOverviewPage canReview={canUseEditorial} />;
      break;
    case 'design':
      content = <TrendsSectionPage title="Design & Förpackning" categories={['design', 'packaging']} />;
      break;
    case 'food':
      content = <TrendsSectionPage title="Mattrender" categories={['food', 'consumer']} />;
      break;
    case 'market':
      content = <TrendsSectionPage title="Marknad & Konkurrens" categories={['market', 'competition']} />;
      break;
    case 'opportunities':
      content = <TrendsOpportunitiesPage />;
      break;
    case 'brand_radar':
      content = <TrendsBrandRadarPage />;
      break;
    case 'editorial':
      content = canUseEditorial ? <TrendsEditorialPage /> : <Alert severity="warning">Du saknar behörighet för den redaktionella vyn.</Alert>;
      break;
    case 'sources':
      content = canManageSources
        ? (
          <TrendsSourceAdminPage
            canAdmin={canAdmin}
            canReview={canUseEditorial}
            canManageSources={canManageSources}
            navigateToView={navigateToView}
          />
        )
        : <Alert severity="warning">Du saknar behörighet för källadministrationen.</Alert>;
      break;
    default:
      content = <TrendsOverviewPage canReview={canUseEditorial} />;
      break;
  }

  return (
    <Box sx={{ bgcolor: TREND_COLORS.pageBg, minHeight: '100vh', mx: -3, mt: -3 }}>
      <Box
        sx={{
          bgcolor: TREND_COLORS.cream,
          borderBottom: `1px solid ${TREND_COLORS.divider}`,
          px: { xs: 2, md: 4 },
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ maxWidth: 1200, mx: 'auto' }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              sx={{
                fontFamily: DISPLAY_FONT,
                fontSize: { xs: '1.3rem', md: '1.6rem' },
                fontWeight: 700,
                letterSpacing: '0.14em',
                color: TREND_COLORS.navy,
                textTransform: 'uppercase',
                marginRight: '65px !important',
                userSelect: 'none',
              }}
            >
              TRENDER
            </Typography>
            {readerNav.map((item) => (
              <Button
                key={item.view}
                size="small"
                variant={view === item.view ? 'contained' : 'text'}
                onClick={() => {
                  navigateToView(item.view);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  px: 1.5,
                  minWidth: 0,
                  whiteSpace: 'pre-line',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  ...(view === item.view ? NAV_SX.active : NAV_SX.inactive),
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center">
            {adminNav.map((item) => (
              <Button
                key={item.view}
                size="small"
                variant={view === item.view ? 'contained' : 'outlined'}
                startIcon={item.icon}
                onClick={() => {
                  navigateToView(item.view);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 1.5,
                  minWidth: 0,
                  ...(view === item.view ? NAV_SX.active : NAV_SX.admin),
                }}
              >
                {item.label}
              </Button>
            ))}
            {onClose && (
              <IconButton
                onClick={onClose}
                sx={{
                  ml: 1,
                  border: `1.5px solid ${TREND_COLORS.divider}`,
                  color: TREND_COLORS.textMuted,
                  width: 34,
                  height: 34,
                  '&:hover': { bgcolor: TREND_COLORS.softNavy, color: TREND_COLORS.navy },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
        {content}
      </Box>
    </Box>
  );
}

export default function TrendsModule(props: TrendsModuleProps) {
  return (
    <TrendChannelProvider>
      <TrendsModuleInner {...props} />
    </TrendChannelProvider>
  );
}
